/**
 * Path-aware docket and page-slug resolution (site-scoped).
 *
 * This platform is single-docket by default: the CMS docket is keyed by hostname
 * alone and the first path segment is the page slug (`/faqs` -> slug "faqs").
 * This site is multi-docket: the first path segment selects a CMS docket keyed
 * by `hostname/<segment>` and the page slug shifts one segment deeper
 * (`/university-of-chicago-settlement/faqs` -> docket
 * "<host>/university-of-chicago-settlement", slug "faqs").
 *
 * These helpers are pure and opt-in: callers pass the configured docket slugs.
 * When the list is empty they reproduce the legacy
 * `pathname.split("/")[1]` and static `domain=<appDocket>` behavior exactly, so
 * the logic is safe to reason about against the single-docket baseline.
 *
 * @module resolveRoute
 */

/**
 * Split a pathname into non-empty segments.
 * @param {string} pathname - Typically `window.location.pathname`.
 * @returns {string[]} Path segments with empty entries removed.
 */
function segments(pathname) {
    return String(pathname || "")
        .split("/")
        .filter(Boolean);
}

/**
 * Return the docket slug for the current path, or "" when none applies.
 *
 * @param {string} pathname - The current pathname.
 * @param {string[]} [docketSlugs] - Configured multi-docket slugs.
 * @returns {string} The matched docket slug, or "" for root / single-docket.
 */
export function currentDocketSlug(pathname, docketSlugs = []) {
    if (!docketSlugs || docketSlugs.length === 0) {
        return "";
    }
    const first = segments(pathname)[0] || "";
    return docketSlugs.includes(first) ? first : "";
}

/**
 * Resolve the page slug from the current path.
 *
 * Single-docket (empty list) returns the first segment, matching the legacy
 * `window.location.pathname.split("/")[1]` behavior (including "" at root).
 * Inside a matched docket the page slug is the second segment.
 *
 * @param {string} pathname - The current pathname.
 * @param {string[]} [docketSlugs] - Configured multi-docket slugs.
 * @returns {string} The page slug ("" denotes the home page).
 */
export function resolvePageSlug(pathname, docketSlugs = []) {
    const parts = segments(pathname);
    if (currentDocketSlug(pathname, docketSlugs)) {
        return parts[1] || "";
    }
    return parts[0] || "";
}

/**
 * Resolve the CMS docket domain used for the `domain=` query parameter.
 *
 * Inside a matched docket this is `<appDocket>/<segment>`; otherwise it is the
 * bare `appDocket`, matching the legacy static behavior.
 *
 * @param {string} appDocket - The site's configured CMS docket (hostname).
 * @param {string} pathname - The current pathname.
 * @param {string[]} [docketSlugs] - Configured multi-docket slugs.
 * @returns {string} The docket domain to query the CMS with.
 */
export function resolveDocketDomain(appDocket, pathname, docketSlugs = []) {
    const docket = currentDocketSlug(pathname, docketSlugs);
    return docket ? `${appDocket}/${docket}` : appDocket;
}

/**
 * Build the full app-docket API URL for the current path.
 *
 * The base (everything before `?`) is preserved so the common appDocket fetch
 * guard, which matches on that base, continues to inspect the payload.
 *
 * @param {string} apiBase - The website endpoint, e.g. `<gateway>/website`.
 * @param {string} appDocket - The site's configured CMS docket (hostname).
 * @param {string} pathname - The current pathname.
 * @param {string[]} [docketSlugs] - Configured multi-docket slugs.
 * @returns {string} The fully-qualified docket API URL.
 */
export function resolveDocketAPI(apiBase, appDocket, pathname, docketSlugs = []) {
    const domain = resolveDocketDomain(appDocket, pathname, docketSlugs);
    return `${apiBase}?domain=${domain}&active=true`;
}

/**
 * Determine whether navigating to a target path crosses a docket boundary.
 *
 * A boundary crossing (root <-> subsite, or subsite A <-> subsite B) requires a
 * full page load so the SPA re-resolves and re-fetches the correct docket,
 * rather than treating the docket segment as a page slug. Always false when no
 * docket slugs are configured.
 *
 * @param {string} currentPathname - The current pathname.
 * @param {string} targetPathname - The pathname of the navigation target.
 * @param {string[]} [docketSlugs] - Configured multi-docket slugs.
 * @returns {boolean} True when the navigation changes the active docket.
 */
export function crossesDocketBoundary(
    currentPathname,
    targetPathname,
    docketSlugs = []
) {
    if (!docketSlugs || docketSlugs.length === 0) {
        return false;
    }
    return (
        currentDocketSlug(currentPathname, docketSlugs) !==
        currentDocketSlug(targetPathname, docketSlugs)
    );
}
